/**
 * The providers served by code in this repository, and the questions asked of a
 * provider id — shared by the backend (`api/spaces/provider.ts` builds its
 * request schemas from this) and the frontend, which must not pull yedra in for
 * a list of strings.
 *
 * Every connectable provider that is NOT in {@link IN_TREE_PROVIDERS} is a
 * sandboxed one, served by a connector package whose manifest claimed the id
 * (`docs/architecture/connector-sandbox.md` §9). That is a structural fact, not
 * a list anybody maintains: a data space can only be created for an id something
 * serves, and a package may never claim an in-tree id ({@link claimsInTreeId}).
 * (A `type='integration'` space's provider column holds a tile id instead —
 * see {@link isSandboxedProvider}.)
 */

export const IN_TREE_PROVIDERS = [
	"seaweed",
	"project",
	"oneData",
	"mcp",
	"httpApi",
] as const;

export type InTreeProvider = (typeof IN_TREE_PROVIDERS)[number];

/**
 * In-tree ids keep their literal types — so a `switch` over them still narrows
 * and `Record<InTreeProvider, …>` tables stay exhaustive — and any other string
 * is a sandboxed provider's.
 */
export type SpaceProvider = InTreeProvider | (string & {});

const IN_TREE_LOWERCASE: ReadonlySet<string> = new Set(
	IN_TREE_PROVIDERS.map((provider) => provider.toLowerCase()),
);

/**
 * Whether `provider` is served in-tree. Exact-case, because every table is keyed
 * on the exact id; {@link claimsInTreeId} is the case-insensitive question.
 */
export const isInTreeProvider = (
	provider: string,
): provider is InTreeProvider =>
	(IN_TREE_PROVIDERS as readonly string[]).includes(provider);

/**
 * Whether a CONNECTABLE provider — one a data space is created for — is a
 * sandboxed one, served (or once served) by a connector package: every such id
 * that is not in-tree.
 *
 * 🔴 Only for a connectable provider. A `type='integration'` space carries an
 * integration tile's id in the same column (`mcp`), which is not
 * in-tree and not a package either — so a question about a space row must also
 * ask `type = 'data'`, as the tool-enablement scope and `integrations/my.ts` do.
 */
export const isSandboxedProvider = (provider: string): boolean =>
	!isInTreeProvider(provider);

/**
 * Whether an id collides with an in-tree provider in any casing — what a package
 * may never claim. Package lookups are case-insensitive (the webhook route only
 * has yedra's lowercased path segment), so a package serving `GitHub` would be
 * found for callbacks meant for the in-tree `github`.
 */
export const claimsInTreeId = (provider: string): boolean =>
	IN_TREE_LOWERCASE.has(provider.toLowerCase());

/**
 * What a provider id may look like: a letter, then letters and digits, at most
 * 64 characters — the shape every id so far has had, and one that is safe in a
 * URL path segment (`/api/webhooks/:provider`), a tool-name prefix and a log line.
 */
export const PROVIDER_ID_PATTERN = /^[A-Za-z][A-Za-z0-9]{0,63}$/;
